import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, valid, invalid, success, error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage('No se proporcionó token de restablecimiento');
      return;
    }
    
    // Verificar token
    fetch(`${API}/api/auth/verify-reset-token/${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.valid) {
          setStatus('valid');
          setEmail(data.email);
        } else {
          setStatus('invalid');
          setMessage(data.message || 'Token inválido o expirado');
        }
      })
      .catch(() => {
        setStatus('invalid');
        setMessage('Error al verificar el token');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      setMessage('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      setMessage('Las contraseñas no coinciden');
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setStatus('success');
      } else {
        setMessage(data.detail || 'Error al restablecer contraseña');
      }
    } catch (error) {
      setMessage('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Verificando */}
        {status === 'verifying' && (
          <div className="bg-zinc-900 rounded-2xl p-8 text-center">
            <Loader2 className="w-12 h-12 text-pink-500 animate-spin mx-auto mb-4" />
            <p className="text-white">Verificando enlace...</p>
          </div>
        )}

        {/* Token inválido */}
        {status === 'invalid' && (
          <div className="bg-zinc-900 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Enlace Inválido</h2>
            <p className="text-gray-400 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-full font-semibold"
            >
              Volver al Login
            </button>
          </div>
        )}

        {/* Formulario de nueva contraseña */}
        {status === 'valid' && (
          <div className="bg-zinc-900 rounded-2xl p-8">
            <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-pink-500" />
            </div>
            <h2 className="text-xl font-bold text-white text-center mb-2">Nueva Contraseña</h2>
            <p className="text-gray-400 text-center mb-6">Ingresa tu nueva contraseña para: <span className="text-pink-400">{email}</span></p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Nueva Contraseña</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-pink-500"
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-300 mb-2">Confirmar Contraseña</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-pink-500"
                  placeholder="Repite la contraseña"
                  required
                />
              </div>
              
              {message && (
                <p className="text-red-400 text-sm text-center">{message}</p>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar Nueva Contraseña'
                )}
              </button>
            </form>
          </div>
        )}

        {/* Éxito */}
        {status === 'success' && (
          <div className="bg-zinc-900 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">¡Contraseña Actualizada!</h2>
            <p className="text-gray-400 mb-6">Ya puedes iniciar sesión con tu nueva contraseña</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-full font-semibold"
            >
              Ir al Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
